/**
 * Styles for the "Anpassa text" document view. The document itself uses the IR
 * preview theme (`previewCss` + `trackChangesCss`), inside a shadow root so
 * neither the app's Tailwind preflight nor the theme leak across; these
 * rules add the editing chrome on top.
 */

import { changeLayersCss, previewCss, trackChangesCss } from '@hyranai/regta-document-ir';

const SANS = `ui-sans-serif,system-ui,-apple-system,'Segoe UI',sans-serif`;

const EDITOR_CSS = `
:host{display:block}
.ft-root{padding:16px;background:#f5f5f4;min-height:100%}
.ft-part-label{max-width:21cm;margin:0 auto 6px;font:500 11px/1.4 ${SANS};color:#57534e;text-transform:uppercase;letter-spacing:.04em}
.ft-node{position:relative;border-radius:2px}
.ft-node[data-hot]{box-shadow:0 0 0 1px #c4b5fd;background:rgba(237,233,254,.25)}
.ft-node[data-editing]{box-shadow:0 0 0 2px #8b5cf6}
.ft-node.ft-flash{box-shadow:0 0 0 2px #a5b4fc;transition:box-shadow .6s}
.ft-tools{display:none;position:absolute;right:0;top:-24px;z-index:20;gap:2px;align-items:center;
  font:500 11px/1 ${SANS};background:#fff;border:1px solid #d6d3d1;border-radius:4px;padding:2px;
  box-shadow:0 2px 6px rgba(0,0,0,.08);white-space:nowrap}
.ft-node[data-hot]>.ft-tools,.ft-tools:focus-within,.ft-tools[data-open]{display:flex}
.ft-btn{font:inherit;border:0;background:transparent;color:#292524;padding:4px 6px;border-radius:3px;cursor:pointer}
.ft-btn:hover{background:#f5f5f4}
.ft-btn:disabled{opacity:.4;cursor:default}
.ft-btn--danger{color:#b91c1c}
.ft-btn--primary{background:#1c1917;color:#fff}
.ft-btn--primary:hover{background:#44403c}
.ft-menu{position:absolute;right:0;top:100%;margin-top:2px;display:flex;flex-direction:column;min-width:200px;
  background:#fff;border:1px solid #d6d3d1;border-radius:4px;padding:2px;box-shadow:0 4px 10px rgba(0,0,0,.1)}
.ft-menu .ft-btn{text-align:left}
.ft-restore{position:absolute;right:0;top:0;z-index:10;font:500 11px/1 ${SANS};background:#fff;border:1px solid #fca5a5;
  color:#991b1b;border-radius:4px;padding:4px 6px;cursor:pointer;text-decoration:none}
.ft-restore:hover{background:#fef2f2}
.ft-restore:disabled{opacity:.5;cursor:default}
.ft-lock{display:inline-flex;align-items:center;gap:4px;padding:4px 6px;color:#57534e}
.ft-lock__icon{flex:none}
.ft-node[data-ft-locked][data-hot]{box-shadow:0 0 0 1px #e7e5e4;background:rgba(245,245,244,.4)}
[data-ft-mark]{position:relative}
[data-ft-mark]::before{content:'';position:absolute;left:-12px;top:2px;bottom:2px;width:2px;border-radius:1px;background:#a78bfa;opacity:.7}
[data-ft-mark='inserted']::before{background:#818cf8}
.rd-doc .rd-value{background:#fef3c7;box-shadow:0 0 0 1px #fcd34d;border-radius:2px}
.rd-doc .rd-blank{background:#fef3c7}
.rd-doc .rd-option--checked>.rd-option__box[data-key]{background:#fef3c7;box-shadow:0 0 0 1px #fcd34d;border-radius:2px}
.rd-doc .rd-option__box[data-key]{cursor:help}
.rd-doc.ft-values [data-key]{cursor:pointer}
.rd-doc.ft-values .rd-value[data-key]:hover,.rd-doc.ft-values .rd-option__box[data-key]:hover{box-shadow:0 0 0 2px #f59e0b}
.rd-hide-adaptations [data-ft-mark]::before{display:none}
.ft-label{display:inline;position:relative}
.ft-row{position:relative}
.ft-row-editor{grid-column:1/-1;display:block}
.ft-editor--inline{display:block;text-align:left;font-weight:normal}
.ft-editor{margin:6px 0 10px;padding:8px;border:1px solid #c4b5fd;border-radius:4px;background:#faf5ff;font:12px/1.5 ${SANS};color:#292524}
.ft-editor label{display:block;font-weight:600;font-size:11px;margin:0 0 4px;color:#57534e;text-transform:uppercase;letter-spacing:.04em}
.ft-editor textarea,.ft-editor input{display:block;width:100%;box-sizing:border-box;font:11px/1.6 Georgia,'Times New Roman',serif;
  border:1px solid #d6d3d1;border-radius:3px;padding:6px;background:#fff;color:#292524;resize:vertical}
.ft-editor input{margin-bottom:6px}
.ft-editor-actions{display:flex;gap:6px;justify-content:flex-end;margin-top:6px;align-items:center}
.ft-editor-hint{margin-right:auto;font-size:11px;color:#78716c}
.ft-note{margin:4px 0;padding:4px 6px;border-radius:3px;font-size:11px}
.ft-note--info{background:#fffbeb;color:#78350f;border:1px solid #fde68a}
.ft-note--warn{background:#fef2f2;color:#991b1b;border:1px solid #fecaca}
.ft-editor{position:relative}
.ft-tokens-wrap{position:relative}
.ft-tokens{display:block;box-sizing:border-box;width:100%;font:11px/1.6 Georgia,'Times New Roman',serif;border:1px solid #d6d3d1;
  border-radius:3px;padding:6px;background:#fff;color:#292524;white-space:pre-wrap;overflow-wrap:anywhere;outline:none;cursor:text}
.ft-tokens:focus{border-color:#8b5cf6;box-shadow:0 0 0 1px #8b5cf6}
.ft-tokens-placeholder{position:absolute;left:7px;top:7px;right:7px;pointer-events:none;color:#a8a29e;font:11px/1.6 Georgia,'Times New Roman',serif}
.ft-chip{display:inline-block;margin:0 1px;padding:0 4px;border-radius:3px;background:#fef3c7;box-shadow:inset 0 0 0 1px #fcd34d;
  color:#78350f;font:500 10.5px/1.5 ${SANS};white-space:nowrap;cursor:default;user-select:all;vertical-align:baseline}
.ft-chipbar{display:flex;align-items:center;gap:8px;margin-top:4px}
.ft-btn--field{white-space:nowrap;flex-shrink:0;border:1px solid #fcd34d;background:#fffbeb;color:#78350f}
.ft-btn--field:hover{background:#fef3c7}
.ft-picker{position:absolute;left:8px;right:8px;z-index:30;margin-top:4px;display:flex;flex-direction:column;gap:4px;max-height:420px;
  background:#fff;border:1px solid #d6d3d1;border-radius:4px;padding:6px;box-shadow:0 6px 16px rgba(0,0,0,.12);font:12px/1.4 ${SANS}}
.ft-editor .ft-picker input,.ft-editor .ft-picker select{font:12px/1.4 ${SANS};margin:0}
.ft-picker__list{overflow:auto;max-height:220px;display:flex;flex-direction:column}
.ft-picker__group{font-weight:600;font-size:10px;text-transform:uppercase;letter-spacing:.04em;color:#78716c;padding:6px 4px 2px}
.ft-picker__item{display:flex;gap:8px;align-items:baseline;width:100%;text-align:left;border:0;background:transparent;padding:4px;border-radius:3px;cursor:pointer;font:inherit;color:#292524}
.ft-picker__item:hover,.ft-picker__item:focus{background:#fef3c7;outline:none}
.ft-picker__label{flex:1;min-width:0}
.ft-picker__preview{flex:none;max-width:45%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#78716c;font-size:11px}
.ft-picker__empty{margin:4px;color:#78716c}
.ft-picker__new{border-top:1px solid #e7e5e4;padding-top:4px}
.ft-picker__row{display:flex;gap:4px}
.ft-editor .ft-picker__row input{flex:1;min-width:0;width:auto}
.ft-picker__row select{border:1px solid #d6d3d1;border-radius:3px;padding:4px;background:#fff}
.ft-picker__hint{margin:4px 0 0;color:#78716c;font-size:11px}
.ft-end{max-width:21cm;margin:12px auto 0;display:flex;align-items:center;gap:8px;font:12px/1.4 ${SANS}}
.ft-btn--link{padding:2px 0;margin-top:6px;color:#5b21b6;text-decoration:underline}
.ft-btn--link:hover{background:transparent;color:#4c1d95}
.ft-comment{margin-top:6px}
.ft-editor .ft-comment textarea{font:12px/1.5 ${SANS}}
[data-ft-badge],.rd-details__row{position:relative}
.ft-cbadge{position:absolute;left:-44px;top:0;z-index:5;display:inline-flex;align-items:center;gap:3px;padding:1px 5px;
  font:600 10px/1.4 ${SANS};color:#5b21b6;background:#f5f3ff;border:1px solid #c4b5fd;border-radius:9px;cursor:pointer}
.ft-cbadge:hover{background:#ede9fe}
.ft-cbadge--inline{position:static;margin-left:4px;vertical-align:baseline}
.ft-cbadge--resolved{color:#78716c;background:#fafaf9;border-color:#e7e5e4}
.ft-btn--add{border:1px dashed #a78bfa;background:#fff;color:#5b21b6;padding:6px 10px}
.ft-btn--add:hover{background:#f5f3ff}
`.trim();

export const finetuneDocumentCss = `${previewCss}\n${trackChangesCss()}\n${changeLayersCss()}\n${EDITOR_CSS}`;
